import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient, type QueryKey } from "@tanstack/react-query";
import { toast } from "sonner";
import moment from "moment-timezone";
import { Label } from "components/ui/label";
import { Textarea } from "components/ui/textarea";
import Form, { Field } from "components/shared/Form";
import Button from "components/shared/Button";
import Avatar from "components/shared/Avatar";
import Select from "components/shared/inputs/Select";
import MoneyInput from "components/shared/inputs/MoneyInput";
import useApi from "utils/api";
import { useCompanyCurrency, useCompanyDateFormat, useCompanyTimeFormat } from "utils/hooks/company";
import { renderSettingsResourceLabel } from "utils/helpers/settings";
import { renderUserName } from "utils/helpers/user";
import { getErrorMessage } from "utils/helpers/errors";
import { useOrgSettingsResourceLabel } from "utils/hooks/useOrgSettingsResourceLabel";
import { useGroups } from "../../../../../Settings/General/OrganisationLevelGroups/context";
import { withLoadingToast } from "../../../../Component/helpers/withLoadingToast";
import { getQualifications } from "../../hooks/useColumns";
import { useQualificationPreferences } from "../../../../Component/hooks/useQualificationPreferences";
import { createEditRequestSchema, type EditRequestFormValues } from "./zodValidation";
import { cn } from "lib/utils";

const FORM_ID = "edit-open-shift-request-form";

const renderList = (values?: any[]) => values?.length
    ? values.map(renderSettingsResourceLabel).join(", ")
    : "—";

interface EditFormProps {
    request: any;
    close: () => void;
    // Requests created by someone else only allow changing the extra payment
    extraPayOnly?: boolean;
    queryOptions: { queryKey: QueryKey };
}

function EditForm({ request, close, extraPayOnly = false, queryOptions }: EditFormProps) {
    const { t } = useTranslation();
    const { authPut } = useApi();
    const queryClient = useQueryClient();
    const currency = useCompanyCurrency();
    const dateFormat = useCompanyDateFormat();
    const timeFormat = useCompanyTimeFormat();
    const { allocatedGroups } = useGroups();
    const qualifications = useQualificationPreferences();
    const renderOrgLevelResourceLabel = useOrgSettingsResourceLabel();
    const schema = useMemo(() => createEditRequestSchema({ t, extraPayOnly }), [t, extraPayOnly]);

    const data = request?.data;
    const schedule = data?.schedule;
    const section = schedule?.sections?.[0];
    const startDate = schedule?.startDate || data?.startDate;
    const endDate = schedule?.endDate || data?.endDate;
    const assignedUsers: any[] = data?.assignedUsers || request?.assignedUsers || [];

    const form = useForm<EditRequestFormValues>({
        defaultValues: {
            type: request?.type ?? "open-shift",
            reason: data?.reason ?? "",
            payType: data?.extraPayment?.payType ?? "fixed",
            payAmount: data?.extraPayment?.payAmount ?? "",
        },
        resolver: zodResolver(schema),
    });

    const mutation = useMutation({
        ...withLoadingToast(t("saving")),
        mutationFn: ({ type, reason, payType, payAmount }: EditRequestFormValues) => {
            const hasExtraPay = payAmount !== "" && payAmount != null;
            const url = extraPayOnly
                ? `/request/open-shift/${request?.id}/extra-payment`
                : `/request/${request?.id}`;
            const requestData = !extraPayOnly ? {
                type,
                data: {
                    ...data,
                    reason,
                    extraPayment: hasExtraPay ? { payType, payAmount } : null,
                    namespace: "schedule",
                },
            } : { extraPayment: { payType, payAmount } }
            return authPut(url, { data: requestData });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryOptions.queryKey });
            toast.success(t(extraPayOnly ? "extra-pay-updated-successfully" : "request-updated"));
            close();
        },
        onError: (error) => getErrorMessage(error, t),
    });

    const onSubmit = useCallback((values: EditRequestFormValues) => mutation.mutate(values), [mutation]);

    const payAmountError = form.formState.errors.payAmount?.message;

    return (
        <Form id={FORM_ID} form={form} onFinish={onSubmit} className="tw-flex-1 tw-min-h-0">
            <div className="tw-flex-1 tw-min-h-0 tw-overflow-y-auto">
                <div className="tw-grid tw-grid-cols-2 tw-gap-4 tw-rounded-lg tw-border tw-p-4 tw-mb-6 tw-text-sm">
                    <div className="tw-flex tw-flex-col tw-gap-1">
                        <span className="tw-text-xs tw-text-muted-foreground">{t("date")}</span>
                        {startDate ? moment(startDate).format(dateFormat) : "—"}
                    </div>
                    <div className="tw-flex tw-flex-col tw-gap-1">
                        <span className="tw-text-xs tw-text-muted-foreground">{t("time")}</span>
                        {startDate && endDate
                            ? `${moment(startDate).format(timeFormat)} - ${moment(endDate).format(timeFormat)}`
                            : "—"}
                    </div>
                    <div className="tw-flex tw-flex-col tw-gap-1">
                        <span className="tw-text-xs tw-text-muted-foreground">{t("job")}</span>
                        {renderSettingsResourceLabel(section?.job || data?.job) || "—"}
                    </div>
                    <div className="tw-flex tw-flex-col tw-gap-1">
                        <span className="tw-text-xs tw-text-muted-foreground">{t("location")}</span>
                        {renderSettingsResourceLabel(section?.location || data?.location) || "—"}
                    </div>
                </div>

                <div className="tw-grid tw-grid-cols-2 tw-gap-4 tw-rounded-lg tw-border tw-p-4 tw-mb-6 tw-text-sm">
                    {qualifications.skills && (
                        <div className="tw-flex tw-flex-col tw-gap-1">
                            <span className="tw-text-xs tw-text-muted-foreground">{t("skills")}</span>
                            {renderList(getQualifications(request, "skills"))}
                        </div>
                    )}
                    {qualifications.licenses && (
                        <div className="tw-flex tw-flex-col tw-gap-1">
                            <span className="tw-text-xs tw-text-muted-foreground">{t("licenses")}</span>
                            {renderList(getQualifications(request, "licenses"))}
                        </div>
                    )}
                    {qualifications.certifications && (
                        <div className="tw-flex tw-flex-col tw-gap-1">
                            <span className="tw-text-xs tw-text-muted-foreground">{t("certifications")}</span>
                            {renderList(getQualifications(request, "certifications"))}
                        </div>
                    )}
                    {allocatedGroups?.map((group: any) => {
                        const level = group.level.replaceAll("_", "");
                        const value = section?.[level] || data?.[level];
                        return (
                            <div key={group.id ?? level} className="tw-flex tw-flex-col tw-gap-1">
                                <span className="tw-text-xs tw-text-muted-foreground">{group.description}</span>
                                {value ? renderOrgLevelResourceLabel(value) : "—"}
                            </div>
                        );
                    })}
                </div>

                {assignedUsers.length > 0 && (
                    <div className="tw-flex tw-flex-col tw-gap-2 tw-rounded-lg tw-border tw-p-4 tw-mb-6 tw-text-sm">
                        <span className="tw-text-xs tw-text-muted-foreground">{t("assigned-users")}</span>
                        <div className="tw-flex tw-flex-wrap tw-gap-2">
                            {assignedUsers.map((assignedUser) => (
                                <div
                                    key={assignedUser?.id}
                                    className="tw-flex tw-items-center tw-gap-2 tw-rounded-full tw-border tw-py-1 tw-pl-1 tw-pr-3"
                                >
                                    <Avatar user={assignedUser} size="sm" />
                                    <span>{renderUserName(assignedUser)}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                <Field name="type" label={t("type")} required={!extraPayOnly}>
                    <Select showSearch={false} disabled={extraPayOnly}>
                        <Select.Item value="open-shift">{t("open-shift")}</Select.Item>
                        <Select.Item value="call-off">{t("call-off")}</Select.Item>
                        {request?.type === "direct-offer" && (
                            <Select.Item value="direct-offer">{t("direct-offer")}</Select.Item>
                        )}
                    </Select>
                </Field>

                <Field name="reason" label={t("reason")} required={!extraPayOnly}>
                    <Textarea placeholder={t("reason")} rows={4} disabled={extraPayOnly} />
                </Field>

                <Field name="payType" label={t("extra-pay")}>
                    <Select showSearch={false}>
                        <Select.Item value="fixed">{t("flat-amount")}</Select.Item>
                        <Select.Item value="hourly">{t("per-hour")}</Select.Item>
                    </Select>
                </Field>

                <div className="tw-flex tw-flex-col tw-gap-2 tw-mb-6">
                    <Label className="tw-font-semibold">{t("amount")}</Label>
                    <Controller
                        control={form.control}
                        name="payAmount"
                        render={({ field }) => (
                            <MoneyInput
                                currency={currency}
                                value={field.value ?? ""}
                                onValueChange={(value) => field.onChange(value ?? "")}
                                placeholder={t("amount")}
                                className={cn(payAmountError && "tw-border-red-500 focus-visible:tw-ring-red-500/20")}
                            />
                        )}
                    />
                    {payAmountError && (
                        <span className="tw-text-red-500 tw-text-sm">{payAmountError}</span>
                    )}
                </div>
            </div>

            <div className="tw-flex tw-items-center tw-justify-end tw-gap-2 tw-pt-4 tw-border-t">
                <Button type="button" variant="ghost" onClick={close} disabled={mutation.isPending}>
                    {t("cancel")}
                </Button>
                <Button type="submit" loading={mutation.isPending} disabled={mutation.isPending}>
                    {t("save")}
                </Button>
            </div>
        </Form>
    );
}

export default EditForm;
